import { Result, err, ok } from 'neverthrow';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import ApiError from './error/apiError';
import NetworkError from './error/networkError';
import ValidationError from './error/validationError';

// Generic type for API responses
export type ApiResult<T> = Result<T, ApiError | NetworkError | ValidationError>;

// Helper function to convert axios errors to our custom error types
function handleAxiosError(error: AxiosError): ApiError | NetworkError {
    if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
        return new NetworkError('Network connection failed', error);
    }

    const status = error.response?.status;
    const data: unknown = error.response?.data;
    const errorField = isRecordWithStringField(data, 'error') ? data.error : undefined;
    const detailsField = isRecordWithStringField(data, 'details') ? data.details : undefined;
    const message = [errorField, detailsField].filter(Boolean).join(': ') || error.message || 'An unexpected error occurred';

    return new ApiError(message, status, error.code, error);
}

// Narrows an unknown API error payload to a specific string field before it's rendered to the user —
// the server always sends { error, details } as strings, but that's not guaranteed for every response shape.
function isRecordWithStringField<K extends string>(value: unknown, field: K): value is Record<K, string> {
    return typeof value === 'object' && value !== null && typeof (value as Record<string, unknown>)[field] === 'string';
}

// Generic GET request wrapper
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
    try {
        const response: AxiosResponse<T> = await axios.get(url, config);
        return ok(response.data);
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return err(handleAxiosError(error));
        }
        return err(new NetworkError('An unexpected error occurred', error));
    }
}

// Generic POST request wrapper
export async function apiPost<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
    try {
        const response: AxiosResponse<T> = await axios.post(url, data, config);
        return ok(response.data);
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return err(handleAxiosError(error));
        }
        return err(new NetworkError('An unexpected error occurred', error));
    }
}

// Generic PUT request wrapper
export async function apiPut<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
    try {
        const response: AxiosResponse<T> = await axios.put(url, data, config);
        return ok(response.data);
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return err(handleAxiosError(error));
        }
        return err(new NetworkError('An unexpected error occurred', error));
    }
}

// Generic PATCH request wrapper
export async function apiPatch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
    try {
        const response: AxiosResponse<T> = await axios.patch(url, data, config);
        return ok(response.data);
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return err(handleAxiosError(error));
        }
        return err(new NetworkError('An unexpected error occurred', error));
    }
}

// Generic DELETE request wrapper
export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
    try {
        const response: AxiosResponse<T> = await axios.delete(url, config);
        return ok(response.data);
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return err(handleAxiosError(error));
        }
        return err(new NetworkError('An unexpected error occurred', error));
    }
}

// Helper function to handle validation errors
export function createValidationError(message: string, field?: string): ValidationError {
    return new ValidationError(message, field);
}

// Helper function to check if an error is a specific type
export function isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
}

export function isNetworkError(error: unknown): error is NetworkError {
    return error instanceof NetworkError;
}

export function isValidationError(error: unknown): error is ValidationError {
    return error instanceof ValidationError;
}
